import {
  Alert,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useParams } from 'react-router-dom'
import { useOrderDocumentAudit } from '@/api/hooks'
import {
  EmptyState,
  ErrorBanner,
  formatMoney,
  InlineSpinner,
  PageHeader,
} from '@/components/common'

/** A figure read from a document, or a dash where that document names nothing here. */
function Figure({ value }: { value?: number }) {
  return (
    <TableCell align="right">
      {value === undefined || value === null ? (
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      ) : (
        formatMoney(value, true)
      )}
    </TableCell>
  )
}

/**
 * The order confirmation, the standing proforma and the standing tax
 * invoice, compared line by line against each other and against today's
 * catalogue rate - so a rate that drifted between the three documents, or
 * away from the master, is seen rather than found by accident.
 */
export function OrderDocumentAuditPage() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading, error } = useOrderDocumentAudit(id)

  if (isLoading) return <InlineSpinner />
  if (error) return <ErrorBanner error={error} />
  if (!data) return <EmptyState message="Nothing to compare." />

  const mismatches = data.lines.filter((line) => line.mismatch).length

  return (
    <>
      <PageHeader
        title={`Document audit — ${data.orderNumber}`}
        subtitle="The order confirmation, the standing proforma and the standing tax invoice, line by line"
      />

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Proforma: {data.proformaNumber ?? 'not raised'} · Final invoice:{' '}
          {data.finalInvoiceNumber ?? 'not raised'}
        </Typography>
      </Box>

      {mismatches > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {mismatches} line{mismatches === 1 ? '' : 's'} disagree between the documents raised on
          this order - highlighted below.
        </Alert>
      ) : (
        <Alert severity="success" sx={{ mb: 2 }}>
          Every document raised on this order agrees, line for line.
        </Alert>
      )}

      {data.lines.length === 0 ? (
        <EmptyState message="This order carries no lines on any document yet." />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2} sx={{ verticalAlign: 'bottom' }}>
                Description
              </TableCell>
              <TableCell align="center" colSpan={3}>
                Confirmation
              </TableCell>
              <TableCell align="center" colSpan={3}>
                Proforma
              </TableCell>
              <TableCell align="center" colSpan={3}>
                Final invoice
              </TableCell>
              <TableCell align="center" colSpan={2}>
                Catalogue today
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell>Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.lines.map((line) => (
              <TableRow
                key={line.description}
                sx={line.mismatch ? { bgcolor: 'warning.50' } : undefined}
              >
                <TableCell>{line.description}</TableCell>
                <TableCell align="right">{line.confirmationQty ?? '—'}</TableCell>
                <Figure value={line.confirmationRate} />
                <Figure value={line.confirmationAmount} />
                <TableCell align="right">{line.proformaQty ?? '—'}</TableCell>
                <Figure value={line.proformaRate} />
                <Figure value={line.proformaAmount} />
                <TableCell align="right">{line.invoiceQty ?? '—'}</TableCell>
                <Figure value={line.invoiceRate} />
                <Figure value={line.invoiceAmount} />
                <Figure value={line.masterRate} />
                <TableCell>
                  {line.masterSource ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={line.masterSource === 'PACKAGE' ? 'Package' : 'Menu item'}
                    />
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      —
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  )
}
